import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { ArrowRight, Mail, Lock, Loader2 } from 'lucide-react';
import ThemeLock from '../components/ThemeLock';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [animStep, setAnimStep] = useState(0);

  useEffect(() => {
    const hasAnimated = sessionStorage.getItem('asap_animated');
    if (hasAnimated) { setAnimStep(5); return; }
    const timers = [
      setTimeout(() => setAnimStep(1), 100),
      setTimeout(() => setAnimStep(2), 240),
      setTimeout(() => setAnimStep(3), 380),
      setTimeout(() => setAnimStep(4), 520),
      setTimeout(() => { setAnimStep(5); sessionStorage.setItem('asap_animated', '1'); }, 700),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container min-h-screen flex flex-col bg-[#F5F5F0]" data-testid="login-page" style={{ colorScheme: 'light' }}>
      <ThemeLock />
      <div className="flex-1 flex flex-col justify-center px-7 pb-8 pt-16">

        {/* Logo animation */}
        <div className="mb-3">
          <h1 className="text-[52px] font-black tracking-[-0.04em] text-[#111111] leading-none" style={{textRendering: 'optimizeLegibility'}}>
            <span className={`inline-block transition-all duration-150 ${animStep >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>a</span>
            <span className={`inline-block transition-all duration-150 ${animStep >= 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>s</span>
            <span className={`inline-block transition-all duration-150 ${animStep >= 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>a</span>
            <span className={`inline-block transition-all duration-150 ${animStep >= 4 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>p</span>
          </h1>
        </div>

        {/* Slogan */}
        <p className={`text-[#9B9EA3] text-sm tracking-wide mb-10 transition-all duration-500 ${animStep >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>
          anywhere. anytime. any plan.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className={`space-y-3.5 transition-all duration-500 delay-100 ${animStep >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Email */}
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B0B3B8]" />
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-white text-[#111111] rounded-2xl pl-11 pr-4 py-[14px] text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] focus:shadow-[0_0_0_3px_rgba(182,230,0,0.18)] placeholder-[#7A7E83] transition-all"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B0B3B8]" />
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full bg-white text-[#111111] rounded-2xl pl-11 pr-4 py-[14px] text-sm border border-black/[0.08] focus:outline-none focus:border-[#6B8A00] focus:shadow-[0_0_0_3px_rgba(182,230,0,0.18)] placeholder-[#7A7E83] transition-all"
            />
          </div>

          {/* Sign In — black text, weight 600 (Q36 #2) */}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#B6E600] text-black font-semibold rounded-full py-[14px] text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all disabled:opacity-60 shadow-[0_2px_12px_rgba(182,230,0,0.25)]"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                Sign In <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        {/* Secondary CTA */}
        <p className={`text-center text-[#9B9EA3] text-sm mt-8 transition-all duration-500 delay-200 ${animStep >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          New here?{' '}
          <Link to="/register" data-testid="go-to-register" className="text-[#6B8A00] font-semibold">
            Join ASAP
          </Link>
        </p>

        {/* Forgot password */}
        <p className={`text-center mt-4 transition-all duration-500 delay-300 ${animStep >= 5 ? 'opacity-100' : 'opacity-0'}`}>
          <Link
            to="/forgot-password"
            data-testid="forgot-password-link"
            className="text-[12px] text-[#6B6E73] hover:text-[#111] transition-colors underline"
          >
            Forgot your password?
          </Link>
        </p>

        {/* Legal footer — public TOS/Privacy access */}
        <p className={`text-center text-[11px] text-[#B0B3B8] mt-6 transition-all duration-500 delay-300 ${animStep >= 5 ? 'opacity-100' : 'opacity-0'}`}>
          By continuing you agree to our{' '}
          <Link to="/terms" data-testid="login-terms-link" className="underline text-[#6B6E73]">Terms</Link>
          {' '}&amp;{' '}
          <Link to="/privacy" data-testid="login-privacy-link" className="underline text-[#6B6E73]">Privacy</Link>.
        </p>
      </div>
    </div>
  );
}
